import { useState } from 'react';
import { createUrlParameters } from '@comeon/utility';
import { requestApi } from '~/services/api.js';
import useInterval from '~/hooks/use-interval.js';
import constants, { FETCH_TYPE_ASYNCH } from '../common/lib/utils/constants.js';

const API_BASE_PATH = process.env.API_BASE_PATH || '';
export const POLL_INTERVAL_DELAY = 3000;

const getDefaultPollUrl = paymentType => {
	return `/PaymentFormDataService/${
		paymentType === constants.DEPOSIT ? 'getDepositResult' : 'getWithdrawResultService'
	}?isMobileDevice=true`;
};

/**
 * @typedef PollResult
 * @type {object}
 * @property {string} date
 * @property {string} state - one of successful|failure|pending
 * @property {number} amount
 * @property {string} paymentType - deposit|withdrawal
 * @property {number} amount
 * @property {number} totalBalance
 * @property {object} pixelTrackingData
 * @property {number} bonusId
 * @property {string} accHistRef
 * @property {string} statusText
 * @property {boolean} isInstantDeposit
 * @property {boolean} hasLastTwoDepositsFailed
 * @property {isFirstDeposit} hasLastTwoDepositsFailed
 * @property {number} paymentMethodId
 * @property {string} paymentMethodName
 * @property {string} baseAmount
 */

/**
 * @typedef PollData
 * @type {object}
 * @property {string} paymentType - deposit|withdrawal
 * @property {string} paymentFetchResultFlow
 * @property {string} fetch_type
 * @property {string} accHistRef
 * @property {string} playerRedirectedToFailUrl
 * @property {string} handlePaymentResultCallback
 * @property {number|undefined} totalTimeToWaitInMs
 */

/**
 * @param {PollData} pollData
 * @returns {{startPolling: function, stopPolling: function, pollResult: PollResult}}
 */
function usePaymentPoll(pollData) {
	const [pollResult, setPollResult] = useState(null);
	const [intervalDelay, setIntervalDelay] = useState(null);
	let data = pollData;
	const defaultServerUrl = getDefaultPollUrl(pollData.paymentType);
	const serverUrl = pollData.paymentFetchResultFlow || pollData.serverUrl || defaultServerUrl;

	if (pollData.fetch_type !== FETCH_TYPE_ASYNCH) {
		data = { accHistRef: pollData.accHistRef };
	}
	const makePoll = async () => {
		try {
			const response = await requestApi(`${API_BASE_PATH}${serverUrl}`, createUrlParameters(data), 'POST', true);
			setPollResult(response);
		} catch (e) {
			console.warn('Payment polling Error!'); // eslint-disable-line
			setPollResult({
				state: constants.ERROR,
				errorMessage: { title: e.message }
			});
		}
	};

	useInterval(() => {
		makePoll();
	}, intervalDelay);

	const startPolling = (delay = POLL_INTERVAL_DELAY) => {
		setIntervalDelay(delay);
	};

	const stopPolling = () => {
		setIntervalDelay(null);
	};

	return { pollResult, startPolling, stopPolling };
}

export default usePaymentPoll;
